"use client";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { enUS, ja } from "date-fns/locale";
import {
  BotIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronsUpDownIcon,
  ExternalLinkIcon,
  FilterIcon,
  Loader2Icon,
  PlusIcon,
  RotateCcwIcon,
  SettingsIcon,
  StickyNoteIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useState,
} from "react";
import type { DateRange } from "react-day-picker";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { DateRangeInput } from "@/components/ui/date-dropdown/date-range-input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { MemoDisplaySettingsFields } from "../memos/memo-display-settings-fields";
import type { MemoDisplaySettings } from "../memos/memo-events";
import {
  featurePublicationScopeLabel,
  publicationScopeLabel,
} from "../memos/memo-publication-scope-label";
import {
  hexColorWithAlpha,
  MemoVisualIcon,
  primarySystemMemoLabel,
  systemMemoLabelColor,
} from "../memos/system-memo-visual";
import {
  DEFAULT_MEMO_COLOR,
  MEMO_COLOR_OPTIONS,
} from "../memos/user-memo-colors";
import type { UserMemoCreateTargetOption } from "../memos/user-memo-create-target-options";
import {
  countActiveMemoListFilters,
  dateFromMemoFilterDay,
  memoAuthorInitials,
  memoDateLabel,
  memoFilterDayFromDate,
  memoFormDateModeFromDates,
  panelDateFilterLabel,
} from "./memo-panel-filter";
import {
  DEFAULT_FILTER,
  defaultFilterForSystemMemoLabels,
  MEMO_BODY_MAX_LENGTH,
  MEMO_SEARCH_DEBOUNCE_MS,
  MEMO_TITLE_MAX_LENGTH,
  type MemoFormState,
  type MemoPanelMemo,
  type MemoSourceFilterValue,
  memoSourceFilterValueForSystemLabelKey,
  memoSourceFilterValuesForSystemLabels,
  type PanelFilter,
  SYSTEM_MEMO_SOURCE_FILTER_VALUE,
  type SystemMemoLabelFilterOption,
  USER_SOURCE_FILTER_VALUE,
  type UserMemoFeatureKind,
} from "./memo-panel-types";
import type { UserMemoContext } from "./workspace-side-panel-mode";

const DATE_FNS_LOCALE_BY_LANG = {
  en: enUS,
  ja,
};

const isExternalUrl = (url: string) => /^https?:\/\//.test(url);

/**
 * textarea の改行操作を壊さず、Mac / Windows の修飾キー付き Enter だけを保存導線へ接続します。
 * IME 変換中や保存中は submit せず、誤送信と二重送信を避けます。
 */
function submitMemoFormWithModifierEnter(
  event: KeyboardEvent<HTMLDivElement>,
  onSave: () => void,
  isSubmitting: boolean,
) {
  if (
    event.key !== "Enter" ||
    (!event.metaKey && !event.ctrlKey) ||
    event.nativeEvent.isComposing ||
    isSubmitting
  ) {
    return;
  }

  event.preventDefault();
  onSave();
}

export function PanelHeader({
  title,
  onBack,
  onClose,
}: {
  title: string;
  onBack?: () => void;
  onClose: () => void;
}) {
  const { i18n } = useLingui();

  return (
    <div className="flex h-11 items-center justify-between border-b px-2.5 sm:px-3">
      <div className="flex min-w-0 items-center gap-1">
        {onBack ? (
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={onBack}
            aria-label={i18n._(msg`Back to memo list`)}
          >
            <ChevronLeftIcon className="size-4" />
          </Button>
        ) : null}
        <div className="flex min-w-0 items-center gap-2">
          <StickyNoteIcon className="size-4 shrink-0 text-muted-foreground" />
          <div className="truncate text-sm font-semibold">{title}</div>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          onClick={onClose}
          aria-label={i18n._(msg`Close side panel`)}
        >
          <XIcon className="size-4" />
        </Button>
      </div>
    </div>
  );
}

export function MemoList({
  memos,
  fetching,
  error,
  filter,
  chartHoveredMemoIds,
  listHoveredMemoId,
  memoDisplaySettings,
  availableAuthors,
  systemMemoLabels,
  featureKind,
  targetKind,
  onFilterChange,
  onFilterQueryChange,
  onMemoDisplaySettingsChange,
  onAdd,
  canAdd,
  onMemoHover,
  onMemoHoverEnd,
  onOpen,
}: {
  memos: MemoPanelMemo[];
  fetching: boolean;
  error?: string;
  filter: PanelFilter;
  chartHoveredMemoIds: ReadonlySet<string>;
  listHoveredMemoId: string | null;
  memoDisplaySettings: MemoDisplaySettings;
  availableAuthors: { id: string; name: string }[];
  systemMemoLabels: SystemMemoLabelFilterOption[];
  featureKind: UserMemoFeatureKind;
  targetKind: UserMemoContext["targetKind"];
  onFilterChange: (filter: PanelFilter) => void;
  onFilterQueryChange: (query: string) => void;
  onMemoDisplaySettingsChange: (settings: MemoDisplaySettings) => void;
  onAdd: () => void;
  canAdd: boolean;
  onMemoHover: (memo: MemoPanelMemo) => void;
  onMemoHoverEnd: () => void;
  onOpen: (memo: MemoPanelMemo) => void;
}) {
  const { i18n } = useLingui();
  const [searchText, setSearchText] = useState(filter.query);
  const sourceFilterValues =
    memoSourceFilterValuesForSystemLabels(systemMemoLabels);
  const activeFilterCount = countActiveMemoListFilters(
    filter,
    sourceFilterValues,
  );

  useEffect(() => {
    setSearchText(filter.query);
  }, [filter.query]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchText !== filter.query) {
        onFilterQueryChange(searchText);
      }
    }, MEMO_SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [filter.query, onFilterQueryChange, searchText]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-b p-3">
        <div className="flex items-center gap-2">
          <Input
            value={searchText}
            placeholder={i18n._(msg`Search memos`)}
            className="h-8"
            onChange={(event) => setSearchText(event.currentTarget.value)}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                className="relative"
                aria-label={i18n._(msg`Memo filters`)}
              >
                <FilterIcon className="size-4" />
                {activeFilterCount > 0 ? (
                  <span className="-right-1 -top-1 absolute flex size-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
                    {activeFilterCount}
                  </span>
                ) : null}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-96 space-y-3 p-3">
              <div className="flex items-center justify-between gap-2">
                <DropdownMenuLabel className="px-0 py-0">
                  <Trans>Memo filters</Trans>
                </DropdownMenuLabel>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  disabled={activeFilterCount === 0}
                  onClick={() =>
                    onFilterChange(
                      defaultFilterForSystemMemoLabels(systemMemoLabels),
                    )
                  }
                >
                  <Trans>Clear</Trans>
                </Button>
              </div>
              <DropdownMenuSeparator />
              <MemoDateFilterControl
                filter={filter}
                onFilterChange={onFilterChange}
              />
              <MemoFilterSelect
                label={i18n._(msg`Author`)}
                value={filter.authorUserID}
                onValueChange={(value) =>
                  onFilterChange({ ...filter, authorUserID: value })
                }
              >
                <SelectItem value="ALL">
                  <Trans>All authors</Trans>
                </SelectItem>
                {availableAuthors.map((author) => (
                  <SelectItem key={author.id} value={author.id}>
                    {author.name}
                  </SelectItem>
                ))}
              </MemoFilterSelect>
              <MemoFilterSelect
                label={i18n._(msg`Publication scope`)}
                value={filter.publicationScope}
                onValueChange={(value) =>
                  onFilterChange({
                    ...filter,
                    publicationScope: value as PanelFilter["publicationScope"],
                  })
                }
              >
                <SelectItem value="ALL">
                  <Trans>All publication scopes</Trans>
                </SelectItem>
                <SelectItem value="VIEW">
                  {publicationScopeLabel("VIEW", featureKind, targetKind)}
                </SelectItem>
                <SelectItem value="PROJECT">
                  {publicationScopeLabel("PROJECT", featureKind)}
                </SelectItem>
                <SelectItem value="FEATURE">
                  {featurePublicationScopeLabel(featureKind)}
                </SelectItem>
                <SelectItem value="WORKSPACE">
                  <Trans>Entire workspace</Trans>
                </SelectItem>
              </MemoFilterSelect>
              <MemoSourceFilterChecklist
                value={filter.sourceFilters ?? DEFAULT_FILTER.sourceFilters}
                systemMemoLabels={systemMemoLabels}
                onChange={(sourceFilters) =>
                  onFilterChange({ ...filter, sourceFilters })
                }
              />
              <MemoFilterSelect
                label={i18n._(msg`Status`)}
                value={filter.deleted}
                onValueChange={(value) =>
                  onFilterChange({
                    ...filter,
                    deleted: value as PanelFilter["deleted"],
                  })
                }
              >
                <SelectItem value="ACTIVE">
                  <Trans>Unarchived only</Trans>
                </SelectItem>
                <SelectItem value="ALL">
                  <Trans>All</Trans>
                </SelectItem>
                <SelectItem value="DELETED">
                  <Trans>Archived only</Trans>
                </SelectItem>
              </MemoFilterSelect>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {filter.dateStart ? (
          <div className="mt-2 flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-2 py-1.5 text-xs">
            <span className="min-w-0 truncate text-muted-foreground">
              <Trans>Filtered by date</Trans>:{" "}
              <span className="font-medium text-foreground">
                {panelDateFilterLabel(filter)}
              </span>
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 shrink-0 px-2 text-xs"
              onClick={() =>
                onFilterChange({ ...filter, dateStart: "", dateEnd: "" })
              }
            >
              <Trans>Clear</Trans>
            </Button>
          </div>
        ) : null}
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <div className="divide-y">
          {fetching && memos.length === 0 ? (
            <MemoListStatusBlock>
              <Loader2Icon className="size-4 animate-spin" />
              <Trans>Loading memos...</Trans>
            </MemoListStatusBlock>
          ) : error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              {error}
            </div>
          ) : memos.length === 0 ? (
            <MemoListStatusBlock>
              <Trans>No memos in this view.</Trans>
            </MemoListStatusBlock>
          ) : (
            memos.map((memo) => {
              const isHovered = listHoveredMemoId
                ? listHoveredMemoId === memo.id
                : chartHoveredMemoIds.has(memo.id);
              return (
                <button
                  key={memo.id}
                  type="button"
                  onClick={() => onOpen(memo)}
                  onMouseEnter={() => onMemoHover(memo)}
                  onMouseLeave={onMemoHoverEnd}
                  onFocus={() => onMemoHover(memo)}
                  onBlur={onMemoHoverEnd}
                  className={cn(
                    "w-full border-l-2 border-l-transparent px-3 py-2.5 text-left transition-colors hover:bg-muted/40",
                    isHovered &&
                      "bg-muted/60 ring-1 ring-inset ring-primary/10",
                  )}
                  style={{
                    borderLeftColor: isHovered
                      ? (memo.color ?? DEFAULT_MEMO_COLOR)
                      : undefined,
                  }}
                >
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-start justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-1.5">
                        <MemoListLeadingIcon memo={memo} />
                        <div className="line-clamp-1 font-semibold text-foreground/90 text-sm">
                          {memo.label}
                        </div>
                      </div>
                      <MemoStateBadges memo={memo} />
                    </div>
                    <div className="mt-1 line-clamp-10 whitespace-pre-wrap text-xs leading-5 text-muted-foreground">
                      {memo.body}
                    </div>
                    <MemoListMetadataLine memo={memo} />
                  </div>
                </button>
              );
            })
          )}
        </div>
      </ScrollArea>
      <PanelFooter className="justify-between">
        <MemoDisplaySettingsPopover
          settings={memoDisplaySettings}
          onChange={onMemoDisplaySettingsChange}
        />
        {canAdd ? (
          <Button type="button" size="sm" className="gap-2" onClick={onAdd}>
            <PlusIcon className="size-4" />
            <Trans>Create memo</Trans>
          </Button>
        ) : null}
      </PanelFooter>
    </div>
  );
}

/**
 * memo 一覧の日付フィルターを、start / end 入力とカレンダーで操作できる compact control として描画します。
 *
 * end 未指定時は単日、end 指定時は範囲として扱い、切り替え専用 UI を置かずに判断します。
 */
function MemoDateFilterControl({
  filter,
  onFilterChange,
}: {
  filter: PanelFilter;
  onFilterChange: (filter: PanelFilter) => void;
}) {
  const { i18n } = useLingui();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const dateFnsLocale =
    DATE_FNS_LOCALE_BY_LANG[
      i18n.locale as keyof typeof DATE_FNS_LOCALE_BY_LANG
    ];
  const selectedRange: DateRange | undefined = filter.dateStart
    ? {
        from: dateFromMemoFilterDay(filter.dateStart),
        to: dateFromMemoFilterDay(filter.dateEnd),
      }
    : undefined;
  const calendarStartMonth = new Date(new Date().getFullYear() - 1, 0, 1);

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-muted-foreground">
        <Trans>Date</Trans>
      </div>
      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverAnchor asChild>
          <div className="grid grid-cols-2 gap-2">
            <DateRangeInput
              value={filter.dateStart}
              placeholder="YYYY-MM-DD"
              aria-label={i18n._(msg`Start date`)}
              iconAriaLabel={i18n._(msg`Open calendar`)}
              className="h-8"
              onIconClick={() => setCalendarOpen(true)}
              onValueChange={(dateStart) =>
                onFilterChange({
                  ...filter,
                  dateStart,
                })
              }
              onClear={() =>
                onFilterChange({ ...filter, dateStart: "", dateEnd: "" })
              }
            />
            <DateRangeInput
              value={filter.dateEnd}
              placeholder="YYYY-MM-DD"
              aria-label={i18n._(msg`End date`)}
              iconAriaLabel={i18n._(msg`Open calendar`)}
              className="h-8"
              onIconClick={() => setCalendarOpen(true)}
              onValueChange={(dateEnd) =>
                onFilterChange({
                  ...filter,
                  dateEnd,
                })
              }
              onClear={() =>
                onFilterChange({
                  ...filter,
                  dateEnd: "",
                })
              }
            />
          </div>
        </PopoverAnchor>
        <PopoverContent align="end" className="w-auto p-0" portal={false}>
          <Calendar
            mode="range"
            selected={selectedRange}
            defaultMonth={selectedRange?.from}
            startMonth={calendarStartMonth}
            onSelect={(range) =>
              onFilterChange({
                ...filter,
                dateStart: memoFilterDayFromDate(range?.from),
                dateEnd: memoFilterDayFromDate(range?.to),
              })
            }
            captionLayout="dropdown"
            className="rounded-md"
            lang={i18n.locale}
            locale={dateFnsLocale}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

function MemoFilterSelect({
  label,
  value,
  onValueChange,
  children,
}: {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className="h-8">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>{children}</SelectContent>
      </Select>
    </div>
  );
}

function MemoSourceFilterChecklist({
  value,
  systemMemoLabels,
  onChange,
}: {
  value: MemoSourceFilterValue[];
  systemMemoLabels: SystemMemoLabelFilterOption[];
  onChange: (value: MemoSourceFilterValue[]) => void;
}) {
  const { i18n } = useLingui();
  const selectedSources = new Set(value);
  const systemMemoLabelSourceValues: MemoSourceFilterValue[] =
    systemMemoLabels.map((label) =>
      memoSourceFilterValueForSystemLabelKey(label.key),
    );
  const allSourceFilterValues =
    memoSourceFilterValuesForSystemLabels(systemMemoLabels);
  const allChecked = allSourceFilterValues.every((source) =>
    selectedSources.has(source),
  );
  const hasChecked = allSourceFilterValues.some((source) =>
    selectedSources.has(source),
  );
  const systemMemoValues: MemoSourceFilterValue[] = [
    SYSTEM_MEMO_SOURCE_FILTER_VALUE,
    ...systemMemoLabelSourceValues,
  ];
  const systemMemoAllChecked = systemMemoValues.every((source) =>
    selectedSources.has(source),
  );
  const systemMemoHasChecked = systemMemoValues.some((source) =>
    selectedSources.has(source),
  );
  const toggleAll = (checked: boolean) => {
    onChange(checked ? [...allSourceFilterValues] : []);
  };
  const toggleSource = (source: MemoSourceFilterValue, checked: boolean) => {
    onChange(
      checked
        ? Array.from(new Set([...value, source]))
        : value.filter((selectedSource) => selectedSource !== source),
    );
  };
  const toggleSystemMemos = (checked: boolean) => {
    const systemMemoValueSet = new Set(systemMemoValues);
    const withoutSystemMemos = value.filter(
      (source) => !systemMemoValueSet.has(source),
    );
    onChange(
      checked
        ? Array.from(new Set([...withoutSystemMemos, ...systemMemoValues]))
        : withoutSystemMemos,
    );
  };

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-muted-foreground">
        <Trans>Memo type</Trans>
      </div>
      <div className="space-y-2 rounded-md border border-border/70 p-2">
        <MemoSourceFilterCheckbox
          label={<Trans>All memo types</Trans>}
          checked={allChecked ? true : hasChecked ? "indeterminate" : false}
          onCheckedChange={(checked) => toggleAll(checked === true)}
        />
        <div className="space-y-2 border-border/70 border-t pt-2">
          <MemoSourceFilterCheckbox
            label={<Trans>User memos</Trans>}
            checked={selectedSources.has(USER_SOURCE_FILTER_VALUE)}
            onCheckedChange={(checked) =>
              toggleSource(USER_SOURCE_FILTER_VALUE, checked === true)
            }
          />
          <MemoSourceFilterCheckbox
            label={<Trans>System memos</Trans>}
            checked={
              systemMemoAllChecked
                ? true
                : systemMemoHasChecked
                  ? "indeterminate"
                  : false
            }
            onCheckedChange={(checked) => toggleSystemMemos(checked === true)}
          />
          {systemMemoLabels.length > 0 ? (
            <div className="ml-5 space-y-2 border-border/70 border-l pl-3">
              {systemMemoLabels.map((label) => {
                const source = memoSourceFilterValueForSystemLabelKey(
                  label.key,
                );
                return (
                  <MemoSourceFilterCheckbox
                    key={label.key}
                    label={
                      <span className="flex min-w-0 items-center gap-2">
                        {label.color ? (
                          <span
                            className="size-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: label.color }}
                          />
                        ) : null}
                        <span className="truncate">
                          {systemMemoLabelFilterOptionLabel(label, i18n.locale)}
                        </span>
                      </span>
                    }
                    checked={selectedSources.has(source)}
                    onCheckedChange={(checked) =>
                      toggleSource(source, checked === true)
                    }
                  />
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function systemMemoLabelFilterOptionLabel(
  label: SystemMemoLabelFilterOption,
  locale: string,
) {
  return (
    label.localizedTexts?.find((text) => text.locale === locale)?.name ||
    label.name ||
    label.key
  );
}

/**
 * filter popover 内の tri-state checkbox 行を、label click でも操作できる形で描画します。
 */
function MemoSourceFilterCheckbox({
  label,
  checked,
  onCheckedChange,
}: {
  label: ReactNode;
  checked: boolean | "indeterminate";
  onCheckedChange: (checked: boolean | "indeterminate") => void;
}) {
  const checkboxId = useId();

  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={checkboxId}
        checked={checked}
        onCheckedChange={onCheckedChange}
      />
      <label
        htmlFor={checkboxId}
        className="cursor-pointer text-sm text-foreground"
      >
        {label}
      </label>
    </div>
  );
}

/**
 * chart 上の memo 表示だけを切り替える compact settings です。
 * side panel の一覧はビューア本体として残し、chart memo の見せ方だけを即時反映します。
 */
function MemoDisplaySettingsPopover({
  settings,
  onChange,
}: {
  settings: MemoDisplaySettings;
  onChange: (settings: MemoDisplaySettings) => void;
}) {
  const { i18n } = useLingui();

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={i18n._(msg`Memo display settings`)}
            >
              <SettingsIcon className="size-4" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          <Trans>Memo display settings</Trans>
        </TooltipContent>
      </Tooltip>
      <PopoverContent align="start" side="top" className="w-80 space-y-4 p-4">
        <div className="text-sm font-semibold">
          <Trans>Memo settings</Trans>
        </div>
        <MemoDisplaySettingsFields settings={settings} onChange={onChange} />
      </PopoverContent>
    </Popover>
  );
}

function MemoListStatusBlock({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-16 items-center justify-center gap-2 p-4 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

function MemoListLeadingIcon({ memo }: { memo: MemoPanelMemo }) {
  if (memo.memoType === "SYSTEM") {
    return (
      <MemoVisualIcon
        memo={memo}
        className="size-5 rounded-md"
        iconClassName="size-3.5"
      />
    );
  }

  return (
    <span
      className="flex size-5 shrink-0 items-center justify-center rounded-md text-white"
      style={{ backgroundColor: memo.color ?? DEFAULT_MEMO_COLOR }}
    >
      <StickyNoteIcon className="size-3" />
    </span>
  );
}

function MemoAuthorAvatar({
  user,
  className,
}: {
  user: { name: string; picture?: string | null };
  className?: string;
}) {
  return (
    <Avatar
      className={cn(
        "size-6 shrink-0 border bg-muted text-[10px] text-muted-foreground",
        className,
      )}
      title={user.name}
    >
      {user.picture ? <AvatarImage alt={user.name} src={user.picture} /> : null}
      <AvatarFallback className="text-[10px]">
        {memoAuthorInitials(user.name)}
      </AvatarFallback>
    </Avatar>
  );
}

function SystemAuthorAvatar({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "flex size-6 shrink-0 items-center justify-center rounded-full border border-purple-200 bg-purple-100 text-purple-700",
        className,
      )}
      title="System"
    >
      <BotIcon className="size-3.5" />
    </span>
  );
}

function MemoListMetadataLine({ memo }: { memo: MemoPanelMemo }) {
  const dateLabel = memoDateLabel(memo);

  return (
    <div className="mt-2 flex min-w-0 items-center justify-end gap-1.5 text-xs text-muted-foreground">
      {dateLabel ? <span className="shrink-0">{dateLabel}</span> : null}
      {memo.memoType === "SYSTEM" ? (
        <SystemAuthorAvatar className="size-5" />
      ) : (
        <MemoAuthorAvatar
          user={memo.updatedBy ?? { name: "System" }}
          className="size-5"
        />
      )}
    </div>
  );
}

/**
 * memo 編集フォームの日付入力を、一覧 filter と同じ入力欄 + カレンダー popover に揃えます。
 * 終了日が空なら単日、終了日が入っていれば期間として保存時に解釈します。
 */
function MemoFormDateControl({
  form,
  startDateHasError,
  endDateHasError,
  onChange,
}: {
  form: MemoFormState;
  startDateHasError?: boolean;
  endDateHasError?: boolean;
  onChange: (form: MemoFormState) => void;
}) {
  const { i18n } = useLingui();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const dateFnsLocale =
    DATE_FNS_LOCALE_BY_LANG[
      i18n.locale as keyof typeof DATE_FNS_LOCALE_BY_LANG
    ];
  const calendarStartMonth = new Date(new Date().getFullYear() - 1, 0, 1);
  const selectedStartDate = dateFromMemoFilterDay(form.startDate);
  const selectedEndDate = dateFromMemoFilterDay(form.endDate);

  return (
    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
      <PopoverAnchor asChild>
        <div className="grid grid-cols-2 gap-2">
          <DateRangeInput
            value={form.startDate}
            placeholder="YYYY-MM-DD"
            aria-label={i18n._(msg`Start date`)}
            aria-invalid={startDateHasError}
            aria-describedby={startDateHasError ? "memo-date-error" : undefined}
            iconAriaLabel={i18n._(msg`Open calendar`)}
            className="h-8"
            onIconClick={() => setCalendarOpen(true)}
            onValueChange={(startDate) =>
              onChange({
                ...form,
                startDate,
              })
            }
            onClear={() =>
              onChange({
                ...form,
                startDate: "",
              })
            }
          />
          <DateRangeInput
            value={form.endDate}
            placeholder="YYYY-MM-DD"
            aria-label={i18n._(msg`End date`)}
            aria-invalid={endDateHasError}
            aria-describedby={endDateHasError ? "memo-date-error" : undefined}
            iconAriaLabel={i18n._(msg`Open calendar`)}
            className="h-8"
            onIconClick={() => setCalendarOpen(true)}
            onValueChange={(endDate) => onChange({ ...form, endDate })}
            onClear={() => onChange({ ...form, endDate: "" })}
          />
        </div>
      </PopoverAnchor>
      <PopoverContent align="end" className="w-auto p-0" portal={false}>
        <Calendar
          mode="range"
          selected={{ from: selectedStartDate, to: selectedEndDate }}
          defaultMonth={selectedStartDate}
          startMonth={calendarStartMonth}
          onSelect={(range) =>
            onChange({
              ...form,
              startDate: memoFilterDayFromDate(range?.from),
              endDate: memoFilterDayFromDate(range?.to),
            })
          }
          captionLayout="dropdown"
          className="rounded-md"
          lang={i18n.locale}
          locale={dateFnsLocale}
        />
      </PopoverContent>
    </Popover>
  );
}

function MemoStateBadges({ memo }: { memo: MemoPanelMemo }) {
  const systemLabel =
    memo.memoType === "SYSTEM" ? primarySystemMemoLabel(memo) : undefined;
  const shouldShowPublicationScopeBadge =
    memo.memoType !== "SYSTEM" && memo.publicationScope !== "VIEW";
  if (!systemLabel && !shouldShowPublicationScopeBadge && !memo.deletedAt)
    return null;
  const systemLabelColor = systemLabel ? systemMemoLabelColor(memo) : undefined;

  return (
    <div className="flex shrink-0 items-center gap-1">
      {systemLabel ? (
        <Badge
          variant="outline"
          className="h-5 shrink-0 whitespace-nowrap rounded-sm bg-transparent px-1.5 font-normal text-[10px] leading-none"
          style={{
            borderColor: hexColorWithAlpha(systemLabelColor, "66"),
            backgroundColor: hexColorWithAlpha(systemLabelColor, "14"),
            color: systemLabelColor,
          }}
        >
          {systemLabel.name ?? systemLabel.key}
        </Badge>
      ) : null}
      {shouldShowPublicationScopeBadge ? (
        <Badge
          variant="outline"
          className="h-5 shrink-0 whitespace-nowrap rounded-sm border-border/60 bg-transparent px-1.5 font-normal text-[10px] text-muted-foreground leading-none"
        >
          {publicationScopeLabel(memo.publicationScope, memo.featureKind)}
        </Badge>
      ) : null}
      {memo.deletedAt ? (
        <Badge variant="destructive">
          <Trans>Archived</Trans>
        </Badge>
      ) : null}
    </div>
  );
}

function MemoDateLine({ memo }: { memo: MemoPanelMemo }) {
  const label = memoDateLabel(memo);
  if (!label) return null;
  return <div className="mt-2 text-xs text-muted-foreground">{label}</div>;
}

function MemoActorLine({
  label,
  memo,
  user,
}: {
  label: ReactNode;
  memo: MemoPanelMemo;
  user?: { name: string; picture?: string | null } | null;
}) {
  const actorName = user?.name ?? "System";
  return (
    <div className="flex items-center gap-2">
      {memo.memoType === "SYSTEM" ? (
        <SystemAuthorAvatar className="size-5" />
      ) : (
        <MemoAuthorAvatar
          user={{ name: actorName, picture: user?.picture }}
          className="size-5"
        />
      )}
      <span>
        {label}: {actorName}
      </span>
    </div>
  );
}

function MemoLinksBlock({
  links,
}: {
  links: NonNullable<MemoPanelMemo["links"]>;
}) {
  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-muted-foreground">
        <Trans>Links</Trans>
      </div>
      <div className="space-y-2">
        {links.map((link) => (
          <Button
            key={`${link.kind}:${link.url}`}
            asChild
            variant="outline"
            size="sm"
            className="h-auto w-full items-start justify-start whitespace-normal px-3 py-2.5 text-left"
          >
            <a
              href={link.url}
              target={isExternalUrl(link.url) ? "_blank" : undefined}
              rel={isExternalUrl(link.url) ? "noopener noreferrer" : undefined}
            >
              <span className="mt-px flex size-4 shrink-0 items-center justify-center text-muted-foreground">
                <ExternalLinkIcon className="size-3.5" />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-xs font-medium">
                  {link.label || link.url}
                </span>
                <span className="block truncate text-[11px] text-muted-foreground">
                  {link.url}
                </span>
              </span>
            </a>
          </Button>
        ))}
      </div>
    </div>
  );
}

export function MemoDetail({
  memo,
  fetching,
  onDelete,
  onRestore,
}: {
  memo: MemoPanelMemo | null;
  fetching: boolean;
  onDelete: (memo: MemoPanelMemo) => void;
  onRestore: (memo: MemoPanelMemo) => void;
}) {
  if (fetching && !memo) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" />
        <Trans>Loading memo...</Trans>
      </div>
    );
  }

  if (!memo) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        <Trans>Memo was not found in the current filter.</Trans>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-4 p-4">
          <div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-2">
                <MemoListLeadingIcon memo={memo} />
                <h2 className="min-w-0 text-base font-semibold leading-5">
                  {memo.label}
                </h2>
              </div>
              <MemoStateBadges memo={memo} />
            </div>
            <MemoDateLine memo={memo} />
          </div>
          <p className="whitespace-pre-wrap text-sm leading-6">{memo.body}</p>
          {memo.links?.length ? (
            <>
              <MemoLinksBlock links={memo.links} />
              <Separator />
            </>
          ) : null}
          <div className="space-y-2 text-xs text-muted-foreground">
            <MemoActorLine
              label={<Trans>Updated by</Trans>}
              memo={memo}
              user={memo.updatedBy}
            />
          </div>
        </div>
      </ScrollArea>
      <PanelFooter className="flex-wrap gap-2">
        {memo.canDelete && !memo.deletedAt ? (
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={() => onDelete(memo)}
          >
            <Trash2Icon className="mr-2 size-4" />
            <Trans>Archive</Trans>
          </Button>
        ) : null}
        {memo.canRestore && memo.deletedAt ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onRestore(memo)}
          >
            <RotateCcwIcon className="mr-2 size-4" />
            <Trans>Unarchive</Trans>
          </Button>
        ) : null}
      </PanelFooter>
    </div>
  );
}

function MemoTargetCombobox({
  id,
  value,
  options,
  onValueChange,
}: {
  id: string;
  value?: string | null;
  options: readonly UserMemoCreateTargetOption[];
  onValueChange: (targetKey: string) => void;
}) {
  const { i18n } = useLingui();
  const [open, setOpen] = useState(false);
  const selectedOption = options.find((option) => option.key === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-9 w-full justify-between px-3 font-normal"
        >
          <span
            className={cn(
              "truncate",
              !selectedOption && "text-muted-foreground",
            )}
          >
            {selectedOption?.label ?? <Trans>Select memo target</Trans>}
          </span>
          <ChevronsUpDownIcon
            className="ml-2 shrink-0 opacity-50"
            data-icon="inline-end"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] max-h-[var(--radix-popover-content-available-height)] p-0"
      >
        <Command>
          <CommandInput
            placeholder={i18n._(msg`Search memo target...`)}
            className="h-9"
          />
          <CommandList
            className="max-h-[min(300px,var(--radix-popover-content-available-height))] overscroll-contain"
            onWheelCapture={(event) => event.stopPropagation()}
            onTouchMoveCapture={(event) => event.stopPropagation()}
          >
            <CommandEmpty>
              <Trans>No memo target found.</Trans>
            </CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.key}
                  value={[option.label, option.context.targetId].join(" ")}
                  onSelect={() => {
                    onValueChange(option.key);
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={cn(
                      "size-4",
                      option.key === value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function MemoForm({
  form,
  formError,
  memo,
  createTargetOptions,
  selectedCreateTargetKey,
  canSelectProjectScope,
  featureKind,
  targetKind,
  onChange,
  onCreateTargetChange,
  onCancel,
  onSave,
  onArchive,
  isSubmitting,
  showDateContext,
}: {
  form: MemoFormState;
  formError: string | null;
  memo?: MemoPanelMemo;
  createTargetOptions?: readonly UserMemoCreateTargetOption[];
  selectedCreateTargetKey?: string | null;
  canSelectProjectScope: boolean;
  featureKind: UserMemoFeatureKind;
  targetKind: UserMemoContext["targetKind"];
  onChange: (form: MemoFormState) => void;
  onCreateTargetChange?: (targetKey: string) => void;
  onCancel: () => void;
  onSave: () => void;
  onArchive?: () => void;
  isSubmitting: boolean;
  showDateContext: boolean;
}) {
  const { i18n } = useLingui();
  const titleLength = [...form.title].length;
  const bodyLength = [...form.body].length;
  const inferredDateMode = memoFormDateModeFromDates(form);
  const dateError =
    formError &&
    [
      i18n._(msg`Start date is required.`),
      i18n._(msg`Date is required.`),
      i18n._(msg`Start date and end date are required.`),
      i18n._(msg`End date must be after or equal to start date.`),
    ].includes(formError)
      ? formError
      : null;
  const endDateOrderError = i18n._(
    msg`End date must be after or equal to start date.`,
  );
  const startDateHasError = Boolean(
    dateError && dateError !== endDateOrderError,
  );
  const endDateHasError = dateError === endDateOrderError;
  const canSelectMemoTarget =
    Boolean(onCreateTargetChange) &&
    Boolean(selectedCreateTargetKey) &&
    (createTargetOptions?.length ?? 0) > 1;
  const shouldShowMemoTargetSelect =
    canSelectMemoTarget && form.publicationScope === "VIEW";
  const viewScopeOptionLabel = canSelectMemoTarget ? (
    <Trans>Selected view only</Trans>
  ) : (
    publicationScopeLabel("VIEW", featureKind, targetKind)
  );

  return (
    <div
      className="flex min-h-0 flex-1 flex-col"
      onKeyDown={(event) =>
        submitMemoFormWithModifierEnter(event, onSave, isSubmitting)
      }
    >
      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-4 p-4">
          {showDateContext && inferredDateMode === "DAY" && form.startDate ? (
            <div className="rounded-md border border-dashed bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
              <Trans>Adding memo for {form.startDate}.</Trans>
            </div>
          ) : showDateContext &&
            inferredDateMode === "RANGE" &&
            form.startDate &&
            form.endDate ? (
            <div className="rounded-md border border-dashed bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
              <Trans>
                Adding memo for {form.startDate} – {form.endDate}.
              </Trans>
            </div>
          ) : null}
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="memo-title">
              <Trans>Title</Trans>
            </label>
            <Input
              id="memo-title"
              value={form.title}
              onChange={(event) =>
                onChange({ ...form, title: event.currentTarget.value })
              }
              maxLength={MEMO_TITLE_MAX_LENGTH}
            />
            <div
              className={cn(
                "text-right text-xs",
                titleLength > MEMO_TITLE_MAX_LENGTH
                  ? "text-destructive"
                  : "text-muted-foreground",
              )}
            >
              {titleLength}/{MEMO_TITLE_MAX_LENGTH}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="memo-body">
              <Trans>Body</Trans>
            </label>
            <Textarea
              id="memo-body"
              value={form.body}
              onChange={(event) =>
                onChange({ ...form, body: event.currentTarget.value })
              }
              rows={8}
              maxLength={MEMO_BODY_MAX_LENGTH}
            />
            <div
              className={cn(
                "text-right text-xs",
                bodyLength > MEMO_BODY_MAX_LENGTH
                  ? "text-destructive"
                  : "text-muted-foreground",
              )}
            >
              {bodyLength}/{MEMO_BODY_MAX_LENGTH}
            </div>
          </div>
          <MemoColorField
            value={form.color}
            onChange={(color) => onChange({ ...form, color })}
          />
          <div className="space-y-2">
            <div className="text-sm font-medium">
              <Trans>Date</Trans>
            </div>
            <MemoFormDateControl
              form={form}
              startDateHasError={startDateHasError}
              endDateHasError={endDateHasError}
              onChange={onChange}
            />
            <div className="text-xs text-muted-foreground">
              <Trans>
                Enter a date or date range. Start date only creates a single-day
                memo.
              </Trans>
            </div>
            {dateError ? (
              <div
                id="memo-date-error"
                role="alert"
                className="text-sm text-destructive"
              >
                {dateError}
              </div>
            ) : null}
          </div>
          <div className="space-y-2">
            <label
              className="text-sm font-medium"
              htmlFor="memo-publication-scope"
            >
              <Trans>Publication scope</Trans>
            </label>
            <Select
              value={form.publicationScope}
              onValueChange={(publicationScope) =>
                onChange({
                  ...form,
                  publicationScope:
                    publicationScope as MemoFormState["publicationScope"],
                })
              }
            >
              <SelectTrigger id="memo-publication-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="VIEW">{viewScopeOptionLabel}</SelectItem>
                {featureKind !== "DASHBOARD" ? (
                  <SelectItem value="PROJECT" disabled={!canSelectProjectScope}>
                    {publicationScopeLabel("PROJECT", featureKind)}
                  </SelectItem>
                ) : null}
                <SelectItem value="FEATURE">
                  {featurePublicationScopeLabel(featureKind)}
                </SelectItem>
                <SelectItem value="WORKSPACE">
                  <Trans>Entire workspace</Trans>
                </SelectItem>
              </SelectContent>
            </Select>
            {!canSelectProjectScope && featureKind !== "DASHBOARD" ? (
              <div className="text-xs text-muted-foreground">
                <Trans>
                  Project scope is available only inside a project chart.
                </Trans>
              </div>
            ) : null}
          </div>
          {shouldShowMemoTargetSelect ? (
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="memo-target">
                <Trans>Memo target</Trans>
              </label>
              <MemoTargetCombobox
                id="memo-target"
                value={selectedCreateTargetKey}
                options={createTargetOptions ?? []}
                onValueChange={(targetKey) => {
                  onCreateTargetChange?.(targetKey);
                  onChange({ ...form, createTargetKey: targetKey });
                }}
              />
            </div>
          ) : null}
          {formError && !dateError ? (
            <div
              role="alert"
              className="rounded-md bg-destructive/10 p-2 text-sm text-destructive"
            >
              {formError}
            </div>
          ) : null}
          {memo ? (
            <div className="space-y-2 text-xs text-muted-foreground">
              <MemoActorLine
                label={<Trans>Updated by</Trans>}
                memo={memo}
                user={memo.updatedBy}
              />
            </div>
          ) : null}
        </div>
      </ScrollArea>
      <PanelFooter className="justify-between">
        <div>
          {memo?.canDelete && !memo.deletedAt && onArchive ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={onArchive}
                  aria-label={i18n._(msg`Archive memo`)}
                >
                  <Trash2Icon className="size-4 text-destructive" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-64">
                <Trans>
                  Archive this memo. Archived memos can be restored later.
                </Trans>
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>
        <div className="flex gap-2">
          {!memo ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              <Trans>Cancel</Trans>
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            className="min-w-24"
            onClick={onSave}
            disabled={isSubmitting}
          >
            {isSubmitting ? <Trans>Saving...</Trans> : <Trans>Save</Trans>}
          </Button>
        </div>
      </PanelFooter>
    </div>
  );
}

function MemoColorField({
  value,
  onChange,
}: {
  value: string;
  onChange: (color: string) => void;
}) {
  const { i18n } = useLingui();
  const colorLabels = [
    i18n._(msg`Purple`),
    i18n._(msg`Red`),
    i18n._(msg`Green`),
    i18n._(msg`Cyan`),
    i18n._(msg`Blue`),
    i18n._(msg`Brown`),
  ];

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium">
        <Trans>Color</Trans>
      </div>
      <div className="flex flex-wrap gap-2" role="radiogroup">
        {MEMO_COLOR_OPTIONS.map((color, index) => {
          const selected = value === color;
          const label = colorLabels[index] ?? color;
          return (
            <button
              key={color}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={i18n._(msg`Select ${label} memo color`)}
              className={cn(
                "flex size-8 items-center justify-center rounded-full border transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                selected ? "scale-105 border-foreground" : "border-transparent",
              )}
              style={{ backgroundColor: color }}
              onClick={() => onChange(color)}
            >
              {selected ? (
                <CheckIcon className="size-4 text-white drop-shadow" />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * メモ系サイドパネルの主要アクションを固定フッターに集約します。
 * スクロール領域と操作ボタンを分離し、作成・詳細などのパネル間で操作位置を揃えます。
 */
function PanelFooter({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-h-14 shrink-0 items-center justify-end gap-2 border-t bg-background px-3 py-2",
        className,
      )}
    >
      {children}
    </div>
  );
}
